import React, { useEffect, useState } from 'react';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import ReactionThreadCompact from './ReactionThreadCompact';
import ReactionThreadFull from './ReactionThreadFull';
import ReactionThreadMinimized from './ReactionThreadMinimized';
import { EditorActions } from '../../editor/editorSlice';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const THREAD_FRAGMENT = gql(`
  fragment ReactionThread_thread on PublicThreadFields {
    id
    ...ReactionThreadFull_thread
    ...ReactionThreadCompact_thread
    ...ReactionThreadMinimized_thread
  }
`);

type ReactionThreadProps = {
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  isFocused?: boolean;
  minimized?: boolean;
  isPopOverOpen?: boolean;
  initialShowingMore?: boolean;
  onResize: () => void;
  size: MagneticWallItemSize;
  widerLineMargin: number;
};

const ReactionThread = ({
  thread,
  isFocused = false,
  minimized = false,
  isPopOverOpen = false,
  initialShowingMore = false,
  onResize,
  size,
  widerLineMargin,
}: ReactionThreadProps) => {
  const dispatch = useAppDispatch();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    onResize?.();
  }, [onResize]);

  useEffect(() => {
    if (isHovered) {
      dispatch(EditorActions.highlightReaction(thread.id));
    } else {
      dispatch(EditorActions.removeMagneticWallHighlight());
    }
  }, [dispatch, thread.id, isHovered]);

  const handleClick = () => {
    if (!isPopOverOpen) {
      dispatch(
        EditorActions.focusReaction({
          id: thread.id,
          isActive: true,
          hasExpandIntent: false,
        }),
      );
    }
  };

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const fullViewProps = {
    onResize,
    thread,
    initialShowingMore,
  };

  return (
    <MagneticWallItem
      id={`thread-${thread.id}`}
      focused={isFocused}
      minimized={minimized}
      isPopOverOpen={isPopOverOpen}
      widerLineMargin={widerLineMargin}
      popOverContent={
        <PopOverContainer noPadding width="auto" overflow="visible">
          <ReactionThreadFull
            {...fullViewProps}
            isExpanded={isFocused}
            isHovered={isHovered}
            withinOverlay
          />
        </PopOverContainer>
      }
      fullView={
        <ReactionThreadFull
          {...fullViewProps}
          isHovered={isHovered}
          isExpanded={isFocused && !minimized}
        />
      }
      onClick={handleClick}
      onBlur={handleBlur}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      minimizedView={<ReactionThreadMinimized thread={thread} />}
      compactView={
        <ReactionThreadCompact
          isFocused={isFocused && !minimized}
          thread={thread}
        />
      }
      size={size}
    />
  );
};

export default ReactionThread;
